import { dispatchEvent } from '$utilities/events';
import { loadScript } from '$utilities/requests';

/* ========================================================================= */
/* Shared variables */
/* ========================================================================= */

const sharedMemory = {
    isRequested: false,
    isLoaded: false,
};

/* ========================================================================= */
/* Player interface */
/* ========================================================================= */

/**
 * @class YoutubePlayer
 *
 * YouTube player adapter used to align all the players to the same interface.
 * 1. Default player config used across the application.
 */
class YoutubePlayer {
    sequence = [];
    timer = null;

    /* [1] */
    defaultConfig = {
        playerVars: {
            rel: 0,
            showinfo: 0,
            color: 'white',
            controls: 1,
        },
        events: {
            onStateChange: event => {
                let that = this;

                const isSubArrayEnd = (A, B) => {
                    if (A.length < B.length) { return false; }
                    let i = 0;
                    while (i < B.length) {
                        if (A[A.length - i - 1] !== B[B.length - i - 1]) { return false; }
                        i++;
                    }
                    return true;
                };


                that.sequence = [...that.sequence, event.data];
                // Mouse seek or Arrow key seek
                if (event.data === 1 && (isSubArrayEnd(that.sequence, [2, 3, 1]) || isSubArrayEnd(that.sequence, [3, 1])) && !that.sequence.includes(-1)) {
                    that.sequence = []; // Reset event sequence
                } else if (event.data === 0) {
                    that?.onEnd();
                }
                else {
                    clearTimeout(that.timer); // Cancel previous event
                    if (event.data !== 3) { // If its not buffering,
                        let timeout = setTimeout(function () { // Start timer
                            if (event.data === 1) {
                                that?.onPlay();
                            }
                            else if (event.data === 2) {
                                that?.onPause(that?.originalPlayer.getCurrentTime());
                            }
                            that.sequence = [];
                        }, 250);
                        that.timer = timeout;
                    }
                }
            },
            onReady: () => {
                this.isReady = true;
            },
        }
    };

    isReady = false;

    /**
     * @method constructor
     * @param {HTMLElement} playerElement - The iframe element.
     * @param {object} config (optional) - The YouTube player config.
     *
     * 1. Loads the resources if they are not requested yet.
     * 2. If the resources are already available, initialize the original player.
     * 3. Otherwise if the resources are not loaded yet, wait for it.
     */
    constructor(playerElement, config = {}) {
        if (!sharedMemory.isRequested) { /* [1] */
            this.loadResources();
        }

        if (sharedMemory.isLoaded) { /* [2] */
            this.init(playerElement, config);
        } else { /* [3] */
            window.addEventListener('youtubeReady', () => this.init(playerElement, config));
        }
    }

    /**
     * @method loadResources
     *
     * Loads the resources necessary to initialize the YouTube player.
     * 1. Once the resources are loaded, communicates the information
     *    to any other stalled player.
     */
    loadResources() {
        sharedMemory.isRequested = true;

        window.onYouTubeIframeAPIReady = () => {
            sharedMemory.isLoaded = true;
            dispatchEvent('youtubeReady'); /* [1] */
        };

        loadScript('https://www.youtube.com/iframe_api');
    }

    /**
     * @method init
     * @param {HTMLElement} playerElement - The iframe element.
     * @param {object} config (optional) - The YouTube player config.
     *
     * Initializes the original player.
     */
    init(playerElement, { playerVars = {}, events = {}, ...remainingConfig }) {
        const queryPrefix = playerElement.src.includes('?') ? '&' : '?';
        playerElement.src = `${playerElement.src}${queryPrefix}modestbranding=1&enablejsapi=1`;

        this.originalPlayer = new window.YT.Player(playerElement, {
            playerVars: {
                ...this.defaultConfig.playerVars,
                ...playerVars
            },
            events: {
                ...this.defaultConfig.events,
                ...events
            },
            ...remainingConfig
        });
    }

    /**
     * @method play
     *
     * Dispatches the play method on the original player.
     */
    play() {
        if (sharedMemory.isLoaded && this.isReady) {
            this.originalPlayer.setVolume(.8);
            this.originalPlayer.playVideo();
        } else {
            this.onReady = () => this.play();
        }
    };

    /**
     * @method playAt
     *
     * Dispatches the play method on the original player at a specified time.
     */
    playAt(time) {
        if (sharedMemory.isLoaded && this.isReady) {
            this.originalPlayer.playVideo();
            this.originalPlayer.seekTo(time);
            this.play();
        } else {
            this.onReady = () => this.playAt(time);
        }
    };

    /**
     * @method pause
     *
     * Dispatches the pause method on the original player.
     */
    pause() {
        this.originalPlayer.pauseVideo();
    };
}

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default YoutubePlayer;
